import React from "react";
import axios from "axios";
import validator from 'validator';
import { notifications } from '@mantine/notifications';
import { Group, Text, Button, Textarea, Box } from '@mantine/core';

import { useLoginContext } from "../../context/loginContext";
import { BE_API_TWO, utils } from "../../utils/globalVariables";
import { stringDivider, sqlDateToHumanDate, getPropInSubLevelRole, safeValue } from "../../utils/globalFunctions";
import { canAccessUserBatalNota, canAccessStepBatalNota, arrayStepsCanBatalNota } from "../../utils/accessing";

import ModalResponse from "../../components/general/ModalResponse";
import ModalTakeAction from "../../components/general/ModalTakeAction";

const NomorNota = ({ stateData, fnSuccessBatalNota }) => {

    const mountedRef = React.useRef(true);
    const { loginContext, handleSetLoginContext } = useLoginContext();

    const notificationId = React.useRef({
        errorSubmitData: 'NomorNota-1',
        successBatalNota: 'NomorNota-2',
    })

    const [data, setData] = React.useState({
        submitLoading: false,
        confirmationModal: false,

        keteranganValue: '',
        keteranganValueError: '',

        modalResponse: {
            show: false,
            type: utils.modalResponse.warning,
            hideOnClickOutSide: false,
            content: ''
        }
    });

    React.useEffect(() => {
        return () => {
            mountedRef.current = false;
        }
    }, [])

    const submitData = () => {
        setData(d => ({ ...d, keteranganValueError: '' }));

        if (validator.isEmpty(data.keteranganValue, { ignore_whitespace: true })) {
            setData(d => ({ ...d, keteranganValueError: 'Keterangan Pembatalan Wajib Diisi.' }));
        } else {
            setData(d => ({ ...d, submitLoading: true }))

            const formDT = new FormData();
            const formDTHeader = { 'Content-Type': 'application/json' }

            formDT.append('json_token', loginContext.syswebapp);
            formDT.append('project_id', stateData.projectId);
            formDT.append('project_process', stateData.projectProcess);
            formDT.append('project_is_cancel', stateData.projectIsCancel);
            formDT.append('keterangan', data.keteranganValue);
            formDT.append('cancel_from_process', stateData.projectProcess);
            formDT.append('cancel_slug', utils.projectProcess.dibatalkan.value);
            formDT.append('steps_can_batal', JSON.stringify(arrayStepsCanBatalNota));

            axios({
                method: 'post',
                url: BE_API_TWO + '/batalkan_project_nota',
                data: formDT,
                headers: formDTHeader,
            }).then((response) => {
                if (!mountedRef.current) return null;

                setData(d => ({ ...d, submitLoading: false, confirmationModal: false }));

                if (response.data.flag_status === 'isset') {
                    fnSuccessBatalNota();

                    notifications.show({
                        id: notificationId.current.successBatalNota,
                        title: utils.notification.success.caption,
                        message: 'Berhasil batalkan nota.',
                        color: utils.notification.success.color,
                        icon: utils.notification.success.icon,
                        withBorder: true,
                        autoClose: true,
                    });
                }

                if (response.data.flag_status === 'error') {
                    notifications.show({
                        id: notificationId.current.errorSubmitData,
                        title: utils.notification.warning.caption,
                        message: response.data.pesan,
                        color: utils.notification.warning.color,
                        icon: utils.notification.warning.icon,
                        withBorder: true,
                        autoClose: true,
                    })
                }

                if (response.data.flag_status === 'no-data') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: true,
                            content: 'Data tidak ditemukan.',
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'masih-ada-stok-item-terpakai') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: true,
                            content: 'Masih ada stok item yang digunakan pada project ini. Batalkan penggunaan stok item untuk membatalkan nota ini.',
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'discount-voucher-applied') {
                    setData(d => ({
                        ...d,
                        modalResponse: {
                            ...d.modalResponse,
                            type: utils.modalResponse.warning,
                            hideOnClickOutSide: true,
                            content: 'Terdapat diskon/voucher yang di apply pada nota ini. Hapus terlebih dahulu diskon/voucher tersebut dari nota ini.',
                            show: true
                        }
                    }))
                }

                if (response.data.flag_status === 'invalid-json-token') {
                    handleSetLoginContext(null);
                }

                if (response.data.flag_status === 'user-inactive') {
                    handleSetLoginContext(null);
                }
            }).catch(error => {
                if (!mountedRef.current) return null;

                setData(d => ({
                    ...d,
                    submitLoading: false,
                    confirmationModal: false
                }))

                notifications.show({
                    id: notificationId.current.errorSubmitData,
                    title: utils.notification.warning.caption,
                    message: error.message,
                    color: utils.notification.warning.color,
                    icon: utils.notification.warning.icon,
                    withBorder: true,
                    autoClose: true,
                })
            })
        }
    }

    return (
        <React.Fragment>
            <Text size='sm' fw={500}>{stateData.data.label}</Text>

            <Group position="apart" mt='.35rem'>
                <Text size='sm'>
                    {stringDivider(stateData.data.value.id, [4, 6, 8], '.', '[Nomor Nota]')}
                </Text>

                {canAccessUserBatalNota({ stateObject: { subLevel: loginContext } }) && canAccessStepBatalNota({ stateActualStep: stateData.projectProcess }) &&
                    <Button
                        size="xs"
                        color="red"
                        variant='light'
                        onClick={() => setData(d => ({ ...d, confirmationModal: true }))}
                    >
                        Batal
                    </Button>
                }
            </Group>

            {stateData.data.value.userCreatedName &&
                <Box mt='.5rem'>
                    <Text size='xs' color="dimmed">
                        {safeValue(stateData.data.value.userCreatedName, '')} {stateData.data.value.userCreatedSubLevel ? `(${getPropInSubLevelRole(stateData.data.value.userCreatedSubLevel, 'caption', '')})` : ''} {stateData.data.value.createdDateTime ? `- ${sqlDateToHumanDate({
                            stateDate: stateData.data.value.createdDateTime,
                            stateWithTime: true,
                            stateLang: 'id',
                            stateIsShort: true,
                            stateDay: false,
                            stateDefaultValue: '',
                            stateDateType: 'day',
                            stateWithSecond: false
                        })}` : ''}
                    </Text>
                </Box>
            }

            <ModalResponse
                stateShow={data.modalResponse.show}
                fnHide={() => setData(d => ({
                    ...d,
                    modalResponse: {
                        ...d.modalResponse,
                        show: false
                    }
                }))}

                stateOverlayZIndex={201}

                stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
                stateType={data.modalResponse.type}
                stateContent={data.modalResponse.content}
            />

            {/* <ModalTakeAction
                stateShow={data.confirmationModal}
                fnHide={() => setData(d => ({ ...d, confirmationModal: false }))}

                stateHideOnClickOutSide={true}
                stateTitle={String('Confirmation').toUpperCase()}
                stateOverlayZIndex={201}

                stateButtonClosePx='md'

                stateButtonActionCaption='Ya, Batalkan'
                stateButtonActionPx='xs'
                stateButtonActionColor="red"
                stateButtonActionLoading={data.submitLoading}
                fnButtonAction={submitData}
            >
                Batalkan nota ini ?
            </ModalTakeAction> */}

            <ModalTakeAction
                stateShow={data.confirmationModal}
                fnHide={() => setData(d => ({ ...d, confirmationModal: false }))}

                stateHideOnClickOutSide={true}
                stateTitle={String('Confirmation').toUpperCase()}
                stateOverlayZIndex={201}

                stateButtonClosePx='md'

                stateButtonActionCaption='Ya, Batalkan'
                stateButtonActionPx='xs'
                stateButtonActionColor="red"
                stateButtonActionLoading={data.submitLoading}
                fnButtonAction={submitData}
            >
                <Text
                    size='sm'
                    color="dimmed"
                    mb='md'
                >
                    Batalkan Nota Ini ?
                </Text>

                <Textarea
                    minRows={2}
                    maxRows={3}

                    value={data.keteranganValue}
                    onChange={(e) =>
                        setData(d => ({
                            ...d,
                            keteranganValue: e.target.value
                        }))
                    }

                    label='Keterangan'
                    placeholder='Keterangan Pembatalan'
                    error={data.keteranganValueError}
                    description=''
                    withAsterisk={true}
                />
            </ModalTakeAction>
        </React.Fragment>
    )
}

export default NomorNota;